import React, { useState, useEffect, useRef, useMemo } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Bar } from "react-chartjs-2";
import { Row, Col, Nav } from "react-bootstrap";
import { loadingCard } from "../../../helpers/loading";

ChartJS.register(ArcElement, Tooltip, Legend);

interface DepartmentItem {
  id: number;
  name: string;
  key: string;
  count: number;
}

interface JobItem {
  id: number;
  name: string;
  department_id: number;
  department: string;
  positions_filled: number;
  positions_open: number;
  positions_total: number;
}

interface JobStackedBarChartProps {
  data: any;
  showHeader?: boolean;
}

export const JobsStackedBarChart = ({ data, showHeader = false }: JobStackedBarChartProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [fetchedDepartments, setFetchedDepartments] = useState<DepartmentItem[]>([]);
  const [departmentGroups, setDepartmentGroups] = useState<Record<number, JobItem[]>>({});
  const [selectedDepartmentKey, setSelectedDepartmentKey] = useState<any>(String(Object.keys(departmentGroups)[0]));
  const chartRef = useRef<any>(null);

  useEffect(() => {
    if (data) {
      const { departments, jobs } = data;
      setFetchedDepartments(departments);
      const deptGroups = groupByDepartment(departments, jobs);
      setDepartmentGroups(deptGroups);
    }
    setIsLoading(false);
  }, [data]);

  function groupByDepartment(depts: DepartmentItem[], jobs: JobItem[]) {
    let deptList: Record<string, JobItem[]> = {};

    depts?.map((dept: DepartmentItem) => {
      const filteredJobs = jobs.filter((job: JobItem) => job.department_id === dept.id);
      deptList[dept.key] = filteredJobs;
    });

    if (deptList) {
      setSelectedDepartmentKey(String(Object.keys(deptList)[0]));
    }
    return deptList;
  }

  const chartData = useMemo(() => {
    const jobs = departmentGroups[selectedDepartmentKey] ?? [];

    return {
      labels: jobs?.length > 0 ? jobs.map((j) => j.name) : ["No data available"],
      datasets: [
        {
          label: "Filled",
          data: jobs.map((j) => j.positions_filled),
          backgroundColor: "#7d9158",
        },
        {
          label: "Open",
          data: jobs.map((j) => j.positions_open),
          backgroundColor: "#da7e39",
        },
      ],
    };
  }, [selectedDepartmentKey, departmentGroups]);

  const options = {
    indexAxis: "y" as const,
    maintainAspectRation: true,
    responsive: true,
    datasets: {
      bar: {
        maxBarThickness: 30,
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: {
          font: {
            size: 14,
          },
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          font: {
            size: 14,
          },
        },
      },
    },
    plugins: {
      title: {
        display: true,
        text: `${selectedDepartmentKey !== undefined ? "" : selectedDepartmentKey} Department`,
        color: "#5a7082",
        font: {
          size: 18,
          weight: 500,
        },
      },
    },
    elements: {
      bar: {
        borderWidth: 1,
        borderRadius: 4,
      },
    },
  };

  if (isLoading) {
    return loadingCard();
  }

  return (
    <>
      {showHeader && <h5 className="app-header-text">Job Assignments</h5>}
      <Row className="justify-content-start mb-5">
        <Col xs={4} lg={3}>
          <Nav variant="pills" className="flex-column">
            {fetchedDepartments?.length > 0 ? (
              fetchedDepartments?.map((dept) => (
                <Nav.Item className="d-grid" key={dept.id}>
                  <Nav.Link
                    eventKey={dept.id}
                    className={selectedDepartmentKey === dept.key ? "nav-vertical-active text-truncate" : "nav-vertical text-truncate"}
                    onClick={() => {
                      setSelectedDepartmentKey(dept.key);
                    }}
                    style={{ textAlign: "left", fontWeight: 500, cursor: "pointer" }}
                  >
                    {dept.key}
                  </Nav.Link>
                </Nav.Item>
              ))
            ) : (
              <Nav.Item className="d-grid" key={0}>
                <Nav.Link className={"nav-vertical-active"} style={{ textAlign: "left", fontWeight: 500, cursor: "pointer" }}>
                  {"None"}
                </Nav.Link>
              </Nav.Item>
            )}
          </Nav>
        </Col>
        <Col xs={8} lg={9}>
          <div style={{ height: `${chartData.labels.length * 50}px`, minHeight: "350px" }}>
            {<Bar ref={chartRef} data={chartData} options={options} />}
          </div>
        </Col>
      </Row>
    </>
  );
};
